import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ShieldCheck, Globe2, Wallet, ArrowRight, Users, Building2, Sprout, Award } from 'lucide-react';

/**
 * FeaturesSection (refonte juin 2026)
 *
 * Avant : 8 features techniques détaillées (Score PSE, MRV, Audits, Carnet,
 *         Saisie Agent, Notifications, etc.) → cognitive overload, pas de persona ciblé.
 *
 * Après : 3 bénéfices business + 1 sélecteur de persona vers pages dédiées.
 *         Approche utilisée par Farmforce, Sourcetrace, Olam AtSource, Indigo Ag.
 *
 * Les features techniques sont déplacées dans les pages persona dédiées
 * (`/pour-les-cooperatives`, `/pour-les-planteurs`, etc.) ou dans les dashboards
 * après login.
 */

const BENEFITS = [
  {
    icon: ShieldCheck,
    badge: 'CONFORMITÉ',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    title: 'Prouvez vos pratiques durables',
    bullet: 'Score Durabilité · MRV temps réel · Audits ARS 1000',
    description:
      'Référentiel scientifique aligné sur le standard ARS 1000. Vos producteurs sont audités par des inspecteurs indépendants, vos données sont vérifiables et exportables à tout moment.',
    color: 'from-emerald-600 to-emerald-700',
  },
  {
    icon: Globe2,
    badge: 'EXPORT',
    badgeColor: 'bg-sky-100 text-sky-700',
    title: 'Sécurisez vos exports EUDR',
    bullet: 'Traçabilité lot par lot · QR code · DDS automatique',
    description:
      'Chaque lot de cacao porte sa preuve : géolocalisation parcelle, polygone, statut déforestation, signatures. Génération automatique de la Due Diligence Statement (Règlement UE 2023/1115).',
    color: 'from-sky-700 to-blue-800',
  },
  {
    icon: Wallet,
    badge: 'REVENU',
    badgeColor: 'bg-amber-100 text-amber-700',
    title: 'Recevez votre Rémunération PSE',
    bullet: 'Paiement Orange Money · Score > 6/10 · Bonus Leader',
    description:
      'Les producteurs vertueux sont récompensés. Calcul transparent basé sur la séquestration carbone, l\'ombrage agroforestier et le score durabilité. Paiements traçables versés directement.',
    color: 'from-amber-600 to-orange-700',
  },
];

const PERSONAS = [
  { key: 'cooperatives', icon: Users, label: 'Coopérative', path: '/pour-les-cooperatives' },
  { key: 'planteurs', icon: Sprout, label: 'Planteur', path: '/pour-les-planteurs' },
  { key: 'exportateurs', icon: Building2, label: 'Exportateur', path: '/pour-les-exportateurs' },
  { key: 'auditeurs', icon: Award, label: 'Auditeur', path: '/pour-les-auditeurs' },
];

const FeaturesSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Intro */}
        <div className="max-w-2xl mb-12 sm:mb-16">
          <Badge className="mb-3 bg-[#1A3622]/10 text-[#1A3622] hover:bg-[#1A3622]/15 border-[#1A3622]/20">
            Comment GreenLink change la donne
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A3622] leading-tight">
            Du terrain à la certification,<br />
            <span className="text-[#d4a574]">tout est prouvable.</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mt-4">
            L'infrastructure de confiance qui transforme vos pratiques agroforestières
            en preuves auditées et en revenus mesurables.
          </p>
        </div>

        {/* 3 bénéfices */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-16">
          {BENEFITS.map(({ icon: Icon, badge, badgeColor, title, bullet, description, color }, idx) => (
            <Card
              key={badge}
              className="border-2 border-gray-100 hover:border-[#1A3622]/20 hover:shadow-xl transition-all duration-300 overflow-hidden group"
              data-testid={`benefit-card-${idx}`}
            >
              <div className={`h-1.5 bg-gradient-to-r ${color}`} />
              <CardContent className="pt-6 pb-6 px-5 sm:px-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${color} text-white shadow-md`}>
                    <Icon className="h-6 w-6" strokeWidth={2} />
                  </div>
                  <Badge className={`${badgeColor} border-0 text-[10px] font-bold tracking-wider`}>
                    {badge}
                  </Badge>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1A3622] mb-2 leading-tight">
                  {title}
                </h3>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  {bullet}
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Sélecteur de persona */}
        <Card className="border-2 border-[#1A3622]/15 bg-gradient-to-br from-[#1A3622] via-[#235043] to-[#0c4a6e] text-white shadow-xl overflow-hidden">
          <CardContent className="py-10 sm:py-12 px-5 sm:px-10">
            <div className="text-center mb-7">
              <p className="text-xs font-bold uppercase tracking-widest text-[#d4a574] mb-2">
                Découvrez ce que GreenLink fait pour vous
              </p>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                Vous êtes <span className="text-[#d4a574]">?</span>
              </h3>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {PERSONAS.map(({ key, icon: Icon, label, path }) => (
                <Button
                  key={key}
                  onClick={() => navigate(path)}
                  className="bg-white/10 hover:bg-[#d4a574] hover:text-[#1A3622] border border-white/20 backdrop-blur-sm h-auto py-5 px-3 flex flex-col items-center gap-2 text-white transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  data-testid={`persona-cta-${key}`}
                >
                  <Icon className="h-7 w-7" strokeWidth={1.8} />
                  <span className="font-bold text-sm sm:text-base">{label}</span>
                  <span className="text-[10px] uppercase tracking-wider opacity-80 flex items-center gap-1">
                    Voir la solution
                    <ArrowRight className="h-3 w-3" />
                  </span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default FeaturesSection;
